import { __assign, __read } from "tslib";
// Restructure the project configuration.
//
// The current API is not in the best structure to consume in the web
// player. Here we restructure the project config to what we think a
// v2 API might look like. This keeps the downstream react/redux code
// cleaner as we deal with any quirks here.
import { find, forEach, get, isNil, map, mapValues, partial, sortBy, transform, cloneDeep, pickBy, toPairs, fromPairs, } from 'lodash-es';
import { convertLegacyFrameToNewFrame, Frame, getSmartContainerData, getFormFieldData, HotspotType, Orientation, } from '@marvelapp/core';
import * as projectProperties from './constants/projectProperties';
import * as contentStatus from './constants/contentStatus';
import * as statusBar from './constants/statusBar';
import * as deviceFrames from './constants/deviceFrames';
import { isIPhone, isIPad } from './utils/deviceDetection';
import { isHeader, isFooter, isTimer, overlapsHeader, overlapsFooter, hasValidDestination, } from './utils/hotspot';
import isBrowserInFullScreenMode from './utils/isBrowserInFullScreenMode';
import isEmail from './utils/isEmail';
import getEmbedUrl from './utils/getEmbedUrl';
import getFullScreen from './utils/getFullScreen';
import * as screenLocation from './constants/screenLocation';
import * as plans from './constants/plans';
export function createPartialProjectConfig(partialProjectConfig) {
    if (!partialProjectConfig) {
        throw new Error('partialProjectConfig is required');
    }
    var device = getDeviceProperties(partialProjectConfig);
    var isPartial = true;
    var _a = processScreens(partialProjectConfig, isPartial), screenMap = _a.screenMap, hotspotMap = _a.hotspotMap, overlayMap = _a.overlayMap;
    return getProjectConfig(partialProjectConfig, device, screenMap, hotspotMap, overlayMap);
}
export function createProjectConfig(origProjectConfig) {
    if (!origProjectConfig) {
        throw new Error('origProjectConfig is required');
    }
    var projectConfig = origProjectConfig;
    var device = getDeviceProperties(projectConfig);
    var isPartial = false;
    var _a = processScreens(projectConfig, isPartial), screenMap = _a.screenMap, hotspotMap = _a.hotspotMap, overlayMap = _a.overlayMap;
    return getProjectConfig(projectConfig, device, screenMap, hotspotMap, overlayMap);
}
function getProjectConfig(origProjectConfig, device, screenMap, hotspotMap, overlayMap) {
    var screens = getScreenSequence(screenMap);
    return __assign(__assign({ origProjectConfig: origProjectConfig,
        screenMap: screenMap,
        hotspotMap: hotspotMap,
        overlayMap: overlayMap, companyData: getCompanyData(origProjectConfig), id: getVanityID(origProjectConfig), numericID: origProjectConfig.id, name: origProjectConfig.name, device: device,
        screens: screens }, getScreenGroups({ origProjectConfig: origProjectConfig, screenMap: screenMap })), { appIcon: getAppIcon(origProjectConfig), 
        // TODO: Remove and use plan instead
        isPro: origProjectConfig.is_pro, plan: getPlan(origProjectConfig), projectUrl: origProjectConfig.resource_uri, pusherChannel: origProjectConfig.pusher_public_channel, pusherAnonChannelPrefix: origProjectConfig.pusher_anon_channel_prefix, isWeb: device.frame === Frame.Web, isResponsive: !parseInt(getProperty(origProjectConfig, 'ui-prj-responsive')), centerImage: !!parseInt(getProperty(origProjectConfig, 'ui-prj-center-overflow')), statusBar: getStatusBarProperties(origProjectConfig), screenBackgroundColour: getProperty(origProjectConfig, 'ui-prj-prototype-background-hex'), backgroundColour: getProperty(origProjectConfig, 'ui-prj-background-hex'), isCommentingEnabled: !!parseInt(getProperty(origProjectConfig, 'prj-comments-enabled')), isNavigationEnabled: !parseInt(getProperty(origProjectConfig, 'ui-prj-disable-frame')), scaleRetinaImages: !!parseInt(getProperty(origProjectConfig, 'ui-prj-retina-scale')), swipeThroughScreens: !parseInt(getProperty(origProjectConfig, projectProperties.DISABLE_SWIPE_THROUGH_SCREENS)), areHotspotHintsEnabled: !parseInt(getProperty(origProjectConfig, projectProperties.DISABLE_HOTSPOT_HINTS)), ownerId: origProjectConfig.owner.id, ownerUsername: origProjectConfig.owner_username, modifiedDate: parseInvalidBackendDate(origProjectConfig.last_active) });
}
function getPlan(origProjectConfig) {
    var company = getCompanyData(origProjectConfig);
    // All real companies have isEnterprise = true and companies
    // attached to pro accounts don't
    if (company.isEnterprise) {
        return plans.COMPANY;
    }
    if (origProjectConfig.is_pro) {
        return plans.PRO;
    }
    return plans.FREE;
}
function getVanityID(conf) {
    return conf.vanity_url.split('/').pop();
}
function getAppIcon(projectConfig) {
    if (!projectConfig.app_icon) {
        return null;
    }
    return (projectConfig.app_icon.preview_thumbnail ||
        projectConfig.app_icon.preview_thumbnail_640);
}
function processScreens(projectConfig, isPartial) {
    var screenMap = getScreenMap(projectConfig.images);
    var hotspotMap = {};
    var overlayMap = {};
    if (!isPartial) {
        forEach(screenMap, function (screen) {
            duplicateHotspots(screenMap, screen);
            screen.allHotspots.forEach(function (hotspot) {
                hotspotMap[hotspot.id] = hotspot;
                if (hotspot.isOverlay) {
                    overlayMap[hotspot.destinationScreenID] =
                        screenMap[hotspot.destinationScreenID];
                }
            });
        });
        forEach(screenMap, function (screen) {
            partitionScreenHotspots(screen);
        });
    }
    return { screenMap: screenMap, hotspotMap: hotspotMap, overlayMap: overlayMap };
}
function getScreenSequence(screenMap) {
    return sortBy(screenMap, function (screen) { return screen.order; });
}
function getScreenMap(screens) {
    // The API returns screens in an object where the key is the display
    // order of the screen.  We convert this to an object where the key
    // is the image ID to make it easier to deal with.
    var screenIDList = map(screens, 'id');
    return transform(screens, function (screenMap, screen, order) {
        /* eslint-disable no-param-reassign */
        screen = createScreen(screen, screenIDList);
        screen.order = parseInt(order, 10);
        screenMap[screen.id] = screen;
    }, {});
}
function createScreen(conf, screenIDList) {
    var screen = {
        uuid: conf.uuid,
        id: conf.id,
        name: conf.display_name || conf.name,
        contentStatus: getContentStatus(conf),
        img: {
            width: conf.width,
            height: conf.height,
            url: conf.url,
            previewURL: conf.thumbnail || conf.url,
        },
        // this is needed by the annotation view
        originalImg: conf,
        colorSamples: mapColorSamples(conf),
        allHotspots: conf.hotspots,
        canvasID: conf.es_canvas_id,
        sourcePlatform: parseInt(conf.source_platform),
        sectionId: conf.section_id,
    };
    processHotspots(screen, screenIDList);
    return screen;
}
function getContentStatus(conf) {
    if (conf.url) {
        // If content is being replaced or there was an error replacing
        // content we show the _existing_ content in the meantime
        return contentStatus.IMAGE_UPLOAD_DONE;
    }
    return conf.status;
}
function processHotspots(screen, screenIDList) {
    var hotspots = screen.allHotspots.map(createHotspot);
    // Detect header & footer
    var realHotspots = hotspots.filter(function (hotspot) {
        if (isHeader(hotspot)) {
            screen.header = {
                y: hotspot.y,
                height: hotspot.y,
                hotspots: [],
            };
            return false;
        }
        if (isFooter(hotspot)) {
            screen.footer = {
                y: hotspot.y,
                height: screen.img.height - hotspot.y,
                hotspots: [],
            };
            return false;
        }
        if (isTimer(hotspot)) {
            screen.timer = __assign({ ms: hotspot.timer * 1000, destScreenID: hotspot.destinationScreenID, transition: hotspot.transition }, hotspot);
            return true;
        }
        if (!hasValidDestination(hotspot, screenIDList)) {
            return false;
        }
        return true;
    });
    screen.allHotspots = realHotspots;
}
function partitionScreenHotspots(screen) {
    screen.hotspots = [];
    screen.fixedHotspots = [];
    screen.allHotspots.forEach(function (hotspot) {
        var _a, _b;
        if (screen.header && overlapsHeader(screen.header, hotspot)) {
            hotspot.location = screenLocation.HEADER;
            screen.header.hotspots.push(hotspot);
        }
        else if (screen.footer && overlapsFooter(screen.footer, hotspot)) {
            hotspot.location = screenLocation.FOOTER;
            screen.footer.hotspots.push(hotspot);
        }
        else if ((_b = (_a = hotspot.smartContainer) === null || _a === void 0 ? void 0 : _a.fixedPosition) === null || _b === void 0 ? void 0 : _b.isEnabled) {
            hotspot.location = screenLocation.FIXED;
            screen.fixedHotspots.push(hotspot);
        }
        else {
            hotspot.location = screenLocation.BODY;
            screen.hotspots.push(hotspot);
        }
    });
    // Fixed hotspots are relative to the whole screen.
    // We need a second loop to reposition them, when screen.header is defined.
    if (screen.header) {
        screen.fixedHotspots.forEach(function (hotspot) {
            hotspot.y += screen.header.y;
            hotspot.y2 += screen.header.y;
        });
    }
}
function createHotspot(conf) {
    var x = conf.x, y = conf.y, x2 = conf.x2, y2 = conf.y2;
    var hotspot = {
        id: conf.id,
        type: conf.type,
        location: conf.location,
        transition: conf.transition === 'none' ? '' : conf.transition,
        timer: conf.timer,
        action: conf.action,
        // TODO use camelCaseUnderscore to automatically convert
        // between python style and js style
        externalUrl: conf.external_url,
        newWindow: conf.new_window,
        destinationScreenID: conf.dest_img_fk,
        ownerScreenID: conf.img_owner_fk,
        // TODO: Find a cleaner way to distinguish hotspots from overlays?
        isOverlay: conf.is_overlay,
        overlayBackgroundColour: conf.overlay_background_colour,
        overlayPosition: conf.overlay_position,
        overlayX: conf.overlay_x,
        overlayY: conf.overlay_y,
        anchorYPos: conf.anchor_y_pos,
        smoothScroll: conf.smooth_scroll,
        stayOnTarget: conf.stay_on_page,
        duplicatedOn: conf.images || [],
        maintainScroll: conf.maintain_scroll,
        isScrollable: conf.overlay_scroll,
        x: x,
        y: y,
        x2: x2,
        y2: y2,
    };
    var externalUrl = conf.external_url;
    if (hotspot.type === HotspotType.Embed) {
        externalUrl = getEmbedUrl(externalUrl);
        hotspot.fullScreen = getFullScreen(conf.external_url);
    }
    if (hotspot.type === HotspotType.Url && isEmail(externalUrl)) {
        externalUrl = "mailto:" + externalUrl;
    }
    hotspot.externalUrl = externalUrl;
    // hovers should not have a transition property set
    if (hotspot.action === 'hover') {
        hotspot.transition = null;
    }
    if (conf.note) {
        var smartContainer = getSmartContainerData(conf.note);
        var formField = getFormFieldData(conf.note);
        if (smartContainer) {
            hotspot = __assign(__assign({}, hotspot), { smartContainer: smartContainer });
        }
        if (formField) {
            hotspot = __assign(__assign({}, hotspot), { formField: formField });
        }
    }
    return hotspot;
}
function duplicateHotspots(screenMap, sourceScreen) {
    sourceScreen.allHotspots.forEach(function (sourceHotspot) {
        sourceHotspot.duplicatedOn.forEach(function (targetScreenID) {
            if (sourceScreen.id !== targetScreenID) {
                var targetScreen = screenMap[targetScreenID];
                if (!targetScreen) {
                    // eslint-disable-next-line no-console
                    console.warn(targetScreenID + " is not a valid screen ID");
                    return;
                }
                var duplicatedHotspot = createDuplicatedHotspot(sourceHotspot, targetScreen);
                targetScreen.allHotspots.push(duplicatedHotspot);
            }
        });
    });
}
function createDuplicatedHotspot(sourceHotspot, targetScreen) {
    return __assign(__assign({}, sourceHotspot), { originScreenID: sourceHotspot.ownerScreenID, ownerScreenID: targetScreen.id, duplicatedOn: [] });
}
function getDeviceProperties(origProjectConfig) {
    var _get = partial(getProperty, origProjectConfig);
    var frame = convertLegacyFrameToNewFrame(_get(projectProperties.DEVICE_FRAME));
    var orientation;
    if (frame === Frame.AppleWatch42) {
        orientation = Orientation.Portrait;
    }
    else {
        orientation =
            _get(projectProperties.DEVICE_ORIENTATION) || Orientation.Portrait;
    }
    var device = {
        frame: frame,
        orientation: orientation,
    };
    return device;
}
function getCompanyData(origProjectConfig) {
    var companyData = origProjectConfig.company_data;
    if (companyData) {
        return {
            id: companyData.id,
            name: companyData.name,
            companyImage: companyData.company_image,
            avatar: companyData.avatar,
            loadingScreenIcon: companyData.loading_screen_icon
                ? companyData.loading_screen_icon
                : null,
            loadingScreenColour: getScreenColour(companyData),
            isOpen: companyData.is_open,
            city: companyData.city,
            country: companyData.country,
            isEnterprise: companyData.is_enterprise,
            isOpenHandoff: companyData.is_open_handoff,
        };
    }
    var ownerCompanyId = get(origProjectConfig, 'owner.company_id');
    if (ownerCompanyId) {
        return {
            id: ownerCompanyId,
            isEnterprise: get(origProjectConfig, 'owner.company_is_enterprise'),
        };
    }
    return {};
}
function getScreenColour(companyData) {
    if (companyData.loading_screen_icon) {
        return companyData.loading_screen_colour;
    }
    return null;
}
function getStatusBarProperties(project) {
    var type = getProperty(project, projectProperties.STATUS_BAR_TYPE_PROPERTY);
    var trimStatusBar = getProperty(project, projectProperties.TRIM_STATUS_BAR_PROPERTY);
    var deviceFrame = getDeviceProperties(project).frame;
    var screenNeedsTrimmed = trimStatusBar === '1' && (isIPhone || isIPad);
    var screenNeedsMovedDown = screenNeedsTrimmed &&
        type === statusBar.STATUS_BAR_BLACK_WHITE_CLEAR &&
        isBrowserInFullScreenMode();
    var bodyNeedsDeviceHeight = (isIPhone || isIPad) &&
        deviceFrame === deviceFrames.IPHONE_X &&
        type === statusBar.STATUS_BAR_BLACK_WHITE_CLEAR &&
        isBrowserInFullScreenMode();
    return {
        appleStatusBarStyle: statusBar.STATUS_BAR_TYPE_MAP[type],
        screenNeedsMovedDown: screenNeedsMovedDown,
        bodyNeedsDeviceHeight: bodyNeedsDeviceHeight,
        trimHeight: screenNeedsTrimmed ? statusBar.getHeight(deviceFrame) : 0,
    };
}
function getProperty(config, name) {
    var prop = find(config.properties, function (p) { return p.name === name; });
    if (!prop) {
        throw new Error("Cannot find property \"" + name + "\"");
    }
    return prop.value;
}
function mapColorSamples(image) {
    return mapValues({
        bottom: image.bottom_pixel_colour,
        top: image.top_pixel_colour,
        statusBar: image.status_pixel_colour,
    }, colorToRGBA);
}
function colorToRGBA(color) {
    return color ? "rgba(" + color + ")" : undefined;
}
function getScreenGroups(_a) {
    var origProjectConfig = _a.origProjectConfig, screenMap = _a.screenMap;
    var sections = get(origProjectConfig, 'sections', []);
    var sectionMap = sections.reduce(function (acc, _a) {
        var id = _a.id, name = _a.name, order = _a.project_seq;
        acc[id] = {
            id: id,
            name: name,
            order: order,
            screens: [],
        };
        return acc;
    }, {});
    var ungroupedScreens = [];
    var groupedScreens = sortBy(sectionMap, function (section) { return section.order; });
    forEach(screenMap, function (screen) {
        if (isNil(screen.sectionId)) {
            ungroupedScreens.push(screen);
        }
        else {
            sectionMap[screen.sectionId].screens.push(screen);
        }
    });
    ungroupedScreens.sort(function (a, b) { return a.order - b.order; });
    forEach(sectionMap, function (screens, sectionId) {
        sectionMap[sectionId].screens.sort(function (a, b) { return a.order - b.order; });
    });
    return {
        ungroupedScreens: ungroupedScreens,
        groupedScreens: groupedScreens,
    };
}
// TODO: this shouldn't be needed as backend should return a valid ISO 8601 string
export function parseInvalidBackendDate(dateString) {
    if (typeof dateString === 'string') {
        var t = dateString.replace('T', ' ').replace('Z', ' ').split(/[- :]/);
        // when t[3], t[4] and t[5] are missing they defaults to zero
        var date = new Date(Date.UTC(t[0], t[1] - 1, t[2], t[3] || 0, t[4] || 0, t[5] || 0));
        return date;
    }
    return null;
}
function trimProjectToSection(origProjectConfig, sectionID) {
    var trimmed = cloneDeep(origProjectConfig);
    var trimmedImages = toPairs(pickBy(trimmed.images, function (_a) {
        var imageSectionID = _a.section_id;
        return imageSectionID === sectionID;
    }));
    trimmedImages.sort(function (_a, _b) {
        var _c = __read(_a, 1), a = _c[0];
        var _d = __read(_b, 1), b = _d[0];
        return parseInt(a) - parseInt(b);
    });
    trimmedImages = trimmedImages.map(function (_a, index) {
        var _b = __read(_a, 2), image = _b[1];
        return [index + 1, image];
    });
    trimmed.images = fromPairs(trimmedImages);
    return trimmed;
}
